import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/ui/Card';
import { referralsService, ReferralStats } from '../services/rewardsService';
import {
  Copy,
  Share2,
  CheckCircle2,
  Clock,
  Users,
  Coins,
  ChevronRight,
  Trophy,
  Link as LinkIcon,
} from 'lucide-react';

const MILESTONE_COLORS = [
  { ring: 'ring-blue-200', bg: 'bg-blue-50', text: 'text-blue-700', achieved: 'bg-blue-600' },
  { ring: 'ring-purple-200', bg: 'bg-purple-50', text: 'text-purple-700', achieved: 'bg-purple-600' },
  { ring: 'ring-orange-200', bg: 'bg-orange-50', text: 'text-orange-700', achieved: 'bg-orange-600' },
  { ring: 'ring-green-200', bg: 'bg-green-50', text: 'text-green-700', achieved: 'bg-green-600' },
];

export default function ReferralsPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      // getStats also creates the code if it doesn't exist
      const [statsData] = await Promise.all([
        referralsService.getStats(),
      ]);
      // If no code yet, fetch it to create one
      if (!statsData.code) {
        await referralsService.getMyCode();
        const refreshed = await referralsService.getStats();
        setStats(refreshed);
      } else {
        setStats(statsData);
      }
    } catch (err) {
      console.error('Error loading referral stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = useCallback(async (text: string, type: 'code' | 'link') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      toast.success(type === 'code' ? 'Cod copiat!' : 'Link copiat!');
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error('Nu s-a putut copia');
    }
  }, []);

  const handleShare = useCallback(async () => {
    if (!stats?.link) return;
    const shareText = `Incearca FiiFit - aplicatia de fitness cu AI! Foloseste linkul meu: ${stats.link}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'FiiFit - Invitatie', text: shareText, url: stats.link });
      } catch {
        // User cancelled share
      }
    } else {
      await copyToClipboard(stats.link, 'link');
    }
  }, [stats?.link, copyToClipboard]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <PageHeader title="Invita Prieteni" />
        <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-28 bg-gray-200 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const validCount = stats?.validUses ?? 0;
  const nextMilestone = stats?.milestones.find(m => !m.achieved);

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <PageHeader title="Invita Prieteni" />

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">

        {/* Hero Banner */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-5 text-white shadow-lg">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold">Invita prieteni</h2>
              <p className="text-blue-100 text-sm mt-0.5">Castiga credite pentru fiecare prieten valid</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white/15 rounded-xl p-3 text-center">
              <div className="text-2xl font-bold">{stats?.totalUses ?? 0}</div>
              <div className="text-xs text-blue-100 mt-0.5">Total invitatii</div>
            </div>
            <div className="bg-white/15 rounded-xl p-3 text-center">
              <div className="text-2xl font-bold">{validCount}</div>
              <div className="text-xs text-blue-100 mt-0.5">Valide</div>
            </div>
            <div className="bg-white/15 rounded-xl p-3 text-center">
              <div className="text-2xl font-bold">{stats?.totalCreditsEarned ?? 0}</div>
              <div className="text-xs text-blue-100 mt-0.5">Credite</div>
            </div>
          </div>
        </div>

        {/* Referral Code Card */}
        {stats?.code && (
          <Card>
            <h3 className="text-sm font-bold text-gray-700 mb-3">Codul tau de invitatie</h3>

            {/* Code Display */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 font-mono text-lg font-bold text-gray-900 tracking-widest text-center">
                {stats.code}
              </div>
              <button
                onClick={() => copyToClipboard(stats.code!, 'code')}
                className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                  copied === 'code'
                    ? 'bg-green-100 text-green-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 active:scale-95'
                }`}
              >
                {copied === 'code' ? <CheckCircle2 className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>

            {/* Link Display */}
            {stats.link && (
              <div className="flex items-center gap-2 mb-4">
                <div className="flex-1 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 overflow-hidden">
                  <LinkIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-xs text-gray-600 truncate font-medium">{stats.link}</span>
                </div>
                <button
                  onClick={() => copyToClipboard(stats.link!, 'link')}
                  className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                    copied === 'link'
                      ? 'bg-green-100 text-green-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 active:scale-95'
                  }`}
                >
                  {copied === 'link' ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            )}

            {/* Share Button */}
            <button
              onClick={handleShare}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white py-3 rounded-xl font-semibold text-sm transition-all shadow-sm"
            >
              <Share2 className="w-4 h-4" />
              Distribuie invitatia
            </button>
          </Card>
        )}

        {/* Milestones */}
        <Card>
          <h3 className="text-sm font-bold text-gray-700 mb-4">Praguri de recompensa</h3>
          <div className="space-y-3">
            {(stats?.milestones ?? []).map((milestone, idx) => {
              const colors = MILESTONE_COLORS[idx % MILESTONE_COLORS.length];
              const isNext = nextMilestone?.count === milestone.count;

              return (
                <div
                  key={milestone.count}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                    milestone.achieved
                      ? 'bg-green-50 border-green-200'
                      : isNext
                      ? `${colors.bg} ${colors.ring} ring-1`
                      : 'bg-gray-50 border-gray-100'
                  }`}
                >
                  {/* Status */}
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    milestone.achieved ? 'bg-green-500' : colors.bg
                  }`}>
                    {milestone.achieved ? (
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    ) : (
                      <Users className={`w-4 h-4 ${colors.text}`} />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${
                        milestone.achieved ? 'text-green-700' : 'text-gray-800'
                      }`}>
                        {milestone.label}
                      </span>
                      {isNext && !milestone.achieved && (
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>
                          Urmator
                        </span>
                      )}
                    </div>
                    {/* Progress bar for next milestone */}
                    {isNext && !milestone.achieved && (
                      <div className="mt-1.5">
                        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${colors.achieved} rounded-full transition-all`}
                            style={{ width: `${Math.min((validCount / milestone.count) * 100, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 mt-0.5 block">
                          {validCount} / {milestone.count} prieteni
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Credits */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Coins className={`w-4 h-4 ${milestone.achieved ? 'text-green-500' : 'text-amber-500'}`} />
                    <span className={`text-sm font-bold ${
                      milestone.achieved ? 'text-green-700' : 'text-amber-600'
                    }`}>
                      {milestone.credits}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* How it works */}
        <Card>
          <h3 className="text-sm font-bold text-gray-700 mb-3">Cum functioneaza</h3>
          <div className="space-y-3">
            {[
              { step: '1', text: 'Copiaza codul sau linkul tau unic de mai sus' },
              { step: '2', text: 'Trimite-l prietenilor tai prin WhatsApp, SMS sau email' },
              { step: '3', text: 'Prietenul se inregistreaza folosind codul sau linkul tau' },
              { step: '4', text: 'Dupa validare, primesti automat creditele promise' },
            ].map(item => (
              <div key={item.step} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {item.step}
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Referral History */}
        {stats && stats.referrals.length > 0 && (
          <Card>
            <h3 className="text-sm font-bold text-gray-700 mb-3">
              Invitatii trimise ({stats.referrals.length})
            </h3>
            <div className="space-y-2">
              {stats.referrals.map(ref => (
                <div
                  key={ref.id}
                  className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    ref.isValid ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {ref.isValid ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <Clock className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800">
                      {[ref.referredUser.firstName, ref.referredUser.lastName].filter(Boolean).join(' ') || 'Utilizator nou'}
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(ref.createdAt).toLocaleDateString('ro-RO')}
                      {ref.isValid ? ' · Validat' : ' · In asteptare'}
                    </div>
                  </div>
                  {ref.creditsAwarded > 0 && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Coins className="w-3.5 h-3.5 text-amber-500" />
                      <span className="text-xs font-bold text-amber-600">+{ref.creditsAwarded}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Empty state for no referrals */}
        {stats && stats.referrals.length === 0 && (
          <Card className="text-center py-10">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Trophy className="w-7 h-7 text-gray-400" />
            </div>
            <h3 className="text-sm font-bold text-gray-700 mb-1">Nicio invitatie inca</h3>
            <p className="text-xs text-gray-500 max-w-xs mx-auto">
              Distribuie codul tau si incepe sa castigi credite pentru fiecare prieten care se alatura.
            </p>
          </Card>
        )}

        {/* Back to Rewards */}
        <button
          onClick={() => navigate('/rewards')}
          className="w-full flex items-center justify-center gap-2 text-blue-600 text-sm font-semibold py-3 hover:bg-blue-50 rounded-xl transition-colors"
        >
          <Trophy className="w-4 h-4" />
          Vezi toate recompensele
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
